import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';
import { Auth } from '@angular/fire/auth';
import { getAuth } from 'firebase/auth';
import { getApp, initializeApp } from 'firebase/app';
// Try importing compat namespaces to provide the namespaced API firebaseui expects
// (RecaptchaVerifier in firebaseui sometimes relies on the compat API).
import firebaseCompat from 'firebase/compat/app';
import 'firebase/compat/auth';
import * as firebaseui from 'firebaseui';
import { GoogleAuthProvider } from 'firebase/auth';
import { Subscription } from 'rxjs';
import { MaterialModule } from '../../shared/material.module';

/**
 * LoginComponent - Handles UI concerns only
 * 
 * Responsibilities:
 * - Display FirebaseUI authentication widget
 * - Show error messages from AuthService
 * - Manage UI carousel and loading states
 * - Handle basic user interactions
 * 
 * Delegates to:
 * - AuthService: All authentication logic, user state management, redirects
 * - AuthGuard: Route protection and access control
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('firebaseUIContainer', { static: true })
  firebaseUIContainer!: ElementRef;

  private ui?: firebaseui.auth.AuthUI;
  private authErrorSubscription?: Subscription;

  // UI state - only what's needed for template
  isFirebaseUILoading = true;
  errorMessage: string | null = null;

  // Carousel properties
  currentSlide = 0;
  slides = [0, 1, 2];
  private carouselInterval?: any;

  // Environment URLs for template access
  urls = {
    logoUrl: `${environment.logoUrl}`,
    mainWebsite: environment.appUrl,
    contactUrl: `${environment.appUrl}/contact`,
    companyWebUrl: environment.companyWebUrl,
    termsOfService: `${environment.appUrl}/terms`,
    privacyPolicy: `${environment.appUrl}/privacy`,
    support: `${environment.appUrl}/support`,
  };

  private auth = inject(Auth);

  constructor(
    public authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private cd: ChangeDetectorRef
  ) {}

  dismissError(): void {
    this.errorMessage = null;
    this.authService.clearAuthError();
  }

  ngOnInit(): void {
    // Start carousel auto-play
    this.startCarousel();

    // Check if user is already authenticated - if so, let AuthService handle redirect
    if (this.authService.isAuthenticated) {
      const returnUrl =
        this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
      this.authService.handlePostLoginRedirect(returnUrl);
      return;
    }

    // Listen to auth errors and display them on the login page
    this.authErrorSubscription = this.authService.authError$.subscribe(
      (error) => {
        if (error) {
          this.errorMessage = error;
          // Clear the error from the service after displaying it
          setTimeout(() => this.authService.clearAuthError(), 100);
        }
      }
    );
  }

  ngAfterViewInit(): void {
    this.initializeFirebaseUI();
  }

  ngOnDestroy(): void {
    // Clean up carousel interval
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
    }

    // Clean up auth error subscription
    if (this.authErrorSubscription) {
      this.authErrorSubscription.unsubscribe();
    }

    // Clean up FirebaseUI
    if (this.ui) {
      this.ui.delete();
    }
  }

  // Carousel methods
  startCarousel(): void {
    this.carouselInterval = setInterval(() => {
      this.nextSlide();
    }, 4000); // Change slide every 4 seconds
  }

  nextSlide(): void {
    this.currentSlide = (this.currentSlide + 1) % this.slides.length;
  }

  goToSlide(index: number): void {
    this.currentSlide = index;
    // Restart auto-play when user manually changes slide
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
      this.startCarousel();
    }
  }

  private initializeFirebaseUI(): void {
    if (!this.firebaseUIContainer?.nativeElement) {
      console.error('FirebaseUI container not available');
      this.isFirebaseUILoading = false;
      return;
    }

    // Clean up any existing UI
    this.ui?.delete();

    try {
      // Ensure a Firebase app exists (provideFirebaseApp should have initialized it,
      // but in some bootstrap orders firebaseui may be constructed before that.
      // Safely initialize only if no default app exists so firebaseui has a backing app.
      try {
        getApp();
      } catch (e) {
        initializeApp(environment.firebase);
      }

      // FirebaseUI (and its Recaptcha flow) expects the namespaced auth API
      // in some code paths (e.g. app.auth()). If the compat SDK is available
      // we initialize it and prefer compat auth so RecaptchaVerifier works.
      let authForUI: any;
      try {
        const compatNs = firebaseCompat as any;
        if (compatNs) {
          // Initialize compat app if none exists yet
          if (!compatNs.apps || compatNs.apps.length === 0) {
            try {
              compatNs.initializeApp(environment.firebase);
            } catch (e) {}
          }
          // If compat provides auth(), use it
          if (typeof compatNs.auth === 'function') {
            try {
              authForUI = compatNs.auth();
            } catch (e) {
              authForUI = undefined;
            }
          }
        }
      } catch (e) {
        // ignore and fallback below
      }

      // Fallback to modular getAuth() if compat isn't available or failed
      if (!authForUI) {
        authForUI = getAuth();
        // ensure authForUI has .app() for any callers expecting auth.app()
        try {
          (authForUI as any).app = () => getApp();
        } catch (e) {}
      }

      this.ui = new firebaseui.auth.AuthUI(authForUI as any);
      this.ui.start(
        this.firebaseUIContainer.nativeElement,
        this.getFirebaseUIConfig()
      );

      // Set loading to false after a short delay to allow FirebaseUI to render
      setTimeout(() => {
        this.isFirebaseUILoading = false;
        // Avoid ExpressionChangedAfterItHasBeenCheckedError in dev mode by
        // notifying Angular that we've updated template-bound state after
        // change detection.
        try {
          this.cd.detectChanges();
        } catch {}
      }, 1000);
    } catch (error) {
      console.error('FirebaseUI initialization error:', error);
      this.isFirebaseUILoading = false;
    }
  }
  getFirebaseUIConfig(): firebaseui.auth.Config {
    const returnUrl =
      this.route.snapshot.queryParams['returnUrl'] || '/dashboard';

    return {
      // Only enable Google sign-in (phone auth removed)
      signInOptions: [GoogleAuthProvider.PROVIDER_ID],
      signInSuccessUrl: returnUrl,
      signInFlow: 'popup',
      credentialHelper: firebaseui.auth.CredentialHelper.GOOGLE_YOLO,
      callbacks: {
        signInSuccessWithAuthResult: (authResult, redirectUrl) => {
          console.log('🚀 [LoginComponent] Sign-in successful via FirebaseUI');
          // Clear any existing errors
          this.dismissError();

          // Let AuthService handle the authentication flow and redirect
          // Return false to prevent FirebaseUI from handling the redirect
          return false;
        },
        signInFailure: (error) => {
          console.error(
            '🚨 [LoginComponent] Firebase UI sign-in failed:',
            error
          );
          this.errorMessage = 'Sign-in failed. Please try again.';
          return Promise.resolve();
        },
      },
      tosUrl: 'http://digivcards.ai/terms-and-conditions',
      privacyPolicyUrl: 'http://digivcards.ai/privacy-policy',
    };
  }

  private getSafeReturnUrl(returnUrl: string): string {
    const allowedUrls = [
      '/dashboard',
      '/profile',
      '/settings',
      '/admin',
      '/users',
      '/cards',
      '/unregistered',
    ];

    if (!returnUrl || returnUrl === '/login' || returnUrl === '/') {
      return '/dashboard';
    }

    const isAllowed = allowedUrls.some(
      (url) => returnUrl === url || returnUrl.startsWith(url + '/')
    );
    return isAllowed ? returnUrl : '/dashboard';
  }
}
